import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { logoUrl } from "@/mocks/homeData";
import {
  mainNavLinks,
  servicesMegaMenuData,
  industriesMegaMenuData,
  solutionsMegaMenuData,
  technologiesMegaMenuData,
  contactDropdownData,
  portfolioDropdownData,
  companyDropdownData,
} from "@/mocks/headerData";
import MegaMenuPanel, {
  serviceIconMap,
  industryIconMap,
  solutionIconMap,
  portfolioIconMap,
  getAccent,
} from "./MegaMenuPanel";

type MegaType = "services" | "industries" | "solutions" | "technologies" | "portfolio" | "company";

function getActiveLabel(pathname: string): string | null {
  if (pathname === "/") return "Home";
  if (
    pathname.startsWith("/about-us") ||
    pathname.startsWith("/our-team") ||
    pathname.startsWith("/life-at-astrojal") ||
    pathname.startsWith("/holiday-list") ||
    pathname.startsWith("/careers")
  )
    return "Company";
  if (pathname.startsWith("/services") || pathname.startsWith("/hire-laravel-developer"))
    return "Services";
  if (pathname.startsWith("/industries")) return "Industries";
  if (pathname.startsWith("/solutions") || pathname.startsWith("/ai-solutions"))
    return "Solutions";
  if (pathname.startsWith("/technologies")) return "Technologies";
  if (pathname.startsWith("/portfolio")) return "Portfolio";
  if (pathname.startsWith("/contact-us")) return "Contact Us";
  if (pathname.startsWith("/blog")) return "Blog";
  return null;
}

export default function Header() {
  const { pathname } = useLocation();
  const activeLabel = getActiveLabel(pathname);

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<MegaType | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<Record<string, boolean>>({});
  const [headerHeight, setHeaderHeight] = useState(80);

  const megaMenuRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Track header height for fixed panel positioning */
  useEffect(() => {
    const update = () => {
      if (megaMenuRef.current) {
        setHeaderHeight(megaMenuRef.current.getBoundingClientRect().height);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [scrolled]);

  /* Scroll handler */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Body scroll lock on mobile menu */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  /* Hover intent handlers */
  const handleMegaEnter = useCallback((type: MegaType) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setActiveMegaMenu(type);
    setActiveDropdown(null);
  }, []);

  const handleMegaLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 200);
  }, []);

  const handleDropdownEnter = useCallback((label: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setActiveDropdown(label);
    setActiveMegaMenu(null);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  }, []);

  /* Click outside */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inHeader = megaMenuRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inHeader && !inPanel) {
        setActiveMegaMenu(null);
        setActiveDropdown(null);
      }
    };
    if (activeMegaMenu || activeDropdown) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [activeMegaMenu, activeDropdown]);

  /* Keyboard: Escape closes everything */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveMegaMenu(null);
        setActiveDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const toggleMobileAccordion = (label: string) => {
    setMobileExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const gradientBtnClass =
    "inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover shadow-lg shadow-[#7C3AED]/20";

  const isAccordionItem = (label: string) =>
    ["Services", "Industries", "Solutions", "Technologies", "Portfolio", "Company"].includes(label);

  const getAccordionData = (label: string) => {
    switch (label) {
      case "Services":
        return servicesMegaMenuData.map((c, i) => ({
          title: c.title,
          icon: serviceIconMap[c.title] || c.icon,
          accentIndex: i,
          items: c.items.map((item) => ({ label: item.label, href: item.href })),
        }));
      case "Industries":
        return industriesMegaMenuData.items.map((ind, i) => ({
          title: ind.name,
          icon: industryIconMap[ind.name] || ind.icon,
          accentIndex: i,
          items: [{ label: "Explore", href: ind.href }],
        }));
      case "Solutions":
        return solutionsMegaMenuData.map((g, i) => ({
          title: g.title,
          icon: solutionIconMap[g.title] || g.icon,
          accentIndex: i,
          items: g.items.map((item) => ({ label: item.label, href: item.href })),
        }));
      case "Technologies":
        return technologiesMegaMenuData.map((g, i) => ({
          title: g.name,
          icon: "ri-code-box-line",
          accentIndex: i,
          items: g.items.map((item) => ({ label: item.name, href: "/technologies" })),
        }));
      case "Portfolio":
        return portfolioDropdownData.items.map((item, i) => ({
          title: item.label,
          icon: portfolioIconMap[item.label] || "ri-folder-line",
          accentIndex: i,
          items: [{ label: "View", href: item.href }],
        }));
      default:
        return [];
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 bg-white ${
          scrolled
            ? "shadow-md shadow-foreground-950/5 border-b border-background-200/60"
            : "border-b border-transparent"
        }`}
        style={{ transitionDuration: "400ms" }}
        ref={megaMenuRef}
      >
        <div className="container-astro relative">
          <div
            className={`flex items-center justify-between transition-all duration-400 ${
              scrolled ? "h-16 md:h-[72px]" : "h-20 md:h-24"
            }`}
          >
            <a href="/" className="flex items-center shrink-0">
              <img
                src={logoUrl}
                alt="AstroJal Technology"
                className="w-[140px] sm:w-[150px] md:w-[170px] lg:w-[180px] h-auto object-contain"
                style={{ maxHeight: "48px" }}
              />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {mainNavLinks.map((link) => {
                const hasMega = link.hasMega && link.megaType;
                const hasDrop = link.hasDropdown;
                const isActive = activeLabel === link.label;
                const isOpen = activeMegaMenu === link.megaType || activeDropdown === link.label;

                return (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => {
                      if (hasMega) handleMegaEnter(link.megaType as MegaType);
                      if (hasDrop) handleDropdownEnter(link.label);
                    }}
                    onMouseLeave={() => {
                      if (hasMega) handleMegaLeave();
                      if (hasDrop) handleDropdownLeave();
                    }}
                  >
                    <a
                      href={link.href}
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap nav-underline ${
                        isActive
                          ? "is-active text-accent-600 font-semibold"
                          : isOpen
                          ? "text-accent-600"
                          : "text-foreground-700 hover:text-accent-600"
                      }`}
                      aria-expanded={isOpen}
                      aria-haspopup={hasMega || hasDrop ? "true" : undefined}
                    >
                      {link.label}
                      {(hasMega || hasDrop) && (
                        <i
                          className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      )}
                    </a>

                    {/* Simple Dropdown */}
                    {hasDrop && activeDropdown === link.label && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
                        <div className="w-64 bg-white rounded-2xl shadow-2xl shadow-foreground-950/10 border border-gray-100/80 py-3 overflow-hidden animate-fade-in-up">
                          {contactDropdownData.items.map((item) => (
                            <a
                              key={item.label}
                              href={item.href}
                              className="group flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-600 hover:text-accent-600 hover:bg-accent-50/50 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-1"
                              role="menuitem"
                            >
                              <i className="ri-arrow-right-s-line text-xs text-foreground-300 group-hover:text-accent-400 transition-colors" />
                              <span>{item.label}</span>
                            </a>
                          ))}
                          {link.label === "Contact Us" && (
                            <div className="px-4 pt-2 pb-2 border-t border-gray-100/50 mt-1">
                              <a
                                href="/contact-us"
                                className={`${gradientBtnClass} w-full justify-center text-xs py-2`}
                              >
                                Let&apos;s Talk
                                <i className="ri-arrow-right-line text-xs btn-arrow" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="hidden lg:block">
              <a href="/contact-us" className={gradientBtnClass}>
                Let&apos;s Talk
                <i className="ri-arrow-right-line text-xs btn-arrow" />
              </a>
            </div>

            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors text-foreground-800 hover:bg-background-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Fixed Mega Menu Panel (desktop only) ── */}
      {!mobileOpen && activeMegaMenu && (
        <div
          ref={panelRef}
          className="fixed inset-x-0 z-50 hidden lg:block pointer-events-none"
          style={{ top: `${headerHeight}px` }}
        >
          <div
            className="pt-3 pointer-events-auto"
            onMouseEnter={() => handleMegaEnter(activeMegaMenu)}
            onMouseLeave={() => handleMegaLeave()}
          >
            <div className="max-w-[1280px] mx-auto px-4">
              <div className="relative animate-mega-in">
                {/* Caret */}
                <div className="absolute -top-[6px] left-1/2 -translate-x-1/2 z-50 pointer-events-none">
                  <div className="relative w-0 h-0">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[7px] border-r-[7px] border-b-[7px] border-l-transparent border-r-transparent border-b-gray-200" />
                    <div className="absolute top-[1px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white" />
                  </div>
                </div>
                {/* Panel */}
                <div
                  className="bg-white rounded-2xl shadow-2xl shadow-foreground-950/10 border border-gray-100/80 p-6 md:p-8 mega-menu-scroll"
                  style={{ maxHeight: `calc(100vh - ${headerHeight}px - 80px)`, overflowY: "auto" }}
                  role="menu"
                  aria-label={activeMegaMenu === "services" ? "Services menu" : `${activeMegaMenu} menu`}
                >
                  <MegaMenuPanel type={activeMegaMenu} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-400 ${
          mobileOpen ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-foreground-950/30 backdrop-blur-sm transition-opacity duration-400 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 bottom-0 w-[85%] max-w-[380px] bg-background-50 shadow-2xl transition-transform duration-400 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between h-16 px-5 border-b border-background-200/60">
            <img
              src={logoUrl}
              alt="AstroJal Technology"
              className="w-[130px] h-auto object-contain"
              style={{ maxHeight: "36px" }}
            />
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div
            className="px-5 py-4 flex flex-col gap-0 overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 140px)" }}
          >
            {mainNavLinks.map((link) => {
              const isAccordion = isAccordionItem(link.label);
              const expanded = mobileExpanded[link.label];
              const isActive = activeLabel === link.label;

              return (
                <div key={link.label} className="border-b border-background-200/50 last:border-0">
                  {isAccordion ? (
                    <>
                      <button
                        onClick={() => toggleMobileAccordion(link.label)}
                        className={`flex items-center justify-between w-full py-3.5 text-base font-medium transition-colors ${
                          isActive ? "text-accent-600" : "text-foreground-800 hover:text-accent-600"
                        }`}
                        aria-expanded={expanded}
                      >
                        <span>{link.label}</span>
                        <i
                          className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 ${
                            expanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`mega-accordion-content ${expanded ? "open" : ""}`}
                      >
                        <div className="pl-3 pb-3 flex flex-col gap-3">
                          {link.label === "Company" &&
                            companyDropdownData.items.map((item) => (
                              <a
                                key={item.label}
                                href={item.href}
                                className="flex items-center gap-2.5 py-1.5 text-sm text-foreground-500 hover:text-accent-600 transition-colors"
                                onClick={() => setMobileOpen(false)}
                              >
                                <span className="w-5 h-5 flex items-center justify-center shrink-0">
                                  <i className={`${item.icon} text-base text-accent-500`} />
                                </span>
                                {item.label}
                              </a>
                            ))}
                          {link.label !== "Company" &&
                            getAccordionData(link.label).map((section) => {
                            const a = getAccent(section.accentIndex);
                            return (
                              <div key={section.title}>
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`w-8 h-8 rounded-lg ${a.iconBg} flex items-center justify-center shrink-0`}>
                                    <i className={`${section.icon} text-sm ${a.iconText}`} />
                                  </div>
                                  <p className="text-sm font-semibold text-foreground-700">
                                    {section.title}
                                  </p>
                                </div>
                                <div className="flex flex-col gap-0.5 pl-10">
                                  {section.items.map((item) => (
                                    <a
                                      key={item.label}
                                      href={item.href}
                                      className={`py-1.5 text-sm text-foreground-500 ${a.hoverText} transition-colors`}
                                      onClick={() => setMobileOpen(false)}
                                    >
                                      {item.label}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <a
                      href={link.href}
                      className={`flex items-center justify-between py-3.5 text-base font-medium transition-colors ${
                        isActive ? "text-accent-600" : "text-foreground-800 hover:text-accent-600"
                      }`}
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </a>
                  )}
                </div>
              );
            })}

            <div className="mt-5 space-y-3">
              <a
                href="/contact-us"
                className={`${gradientBtnClass} w-full justify-center text-base py-3.5 rounded-xl`}
                onClick={() => setMobileOpen(false)}
              >
                Let&apos;s Talk
                <i className="ri-arrow-right-line text-sm btn-arrow" />
              </a>
              <a
                href="/hire-laravel-developer"
                className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 bg-transparent border-2 border-[#2563EB]/20 text-[#2563EB] text-base font-semibold rounded-xl hover:bg-[#2563EB]/5 transition-all duration-300"
                onClick={() => setMobileOpen(false)}
              >
                Hire Laravel Developer
                <i className="ri-arrow-right-line text-sm" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}